import { CVEditingSection, FullCV, TemplateId } from '@cibli/shared';
import { useEffect, useMemo, useState } from 'react';

import ComplementaryInfoForm from '../components/form/ComplementaryInfoForm';
import EducationForm from '../components/form/EducationForm';
import ExperienceForm from '../components/form/ExperiencesForm.tsx';
import HobbiesAndInterestsForm from '../components/form/HobbiesAndInterestsForm';
import LanguagesForm from '../components/form/LanguagesForm';
import PersonalExperienceForm from '../components/form/PersonalExperienceForm';
import PersonalInfoForm from '../components/form/PersonalInfoForm';
import SkillsForm from '../components/form/SkillsForm';
import SummaryForm from '../components/form/SummaryForm';
import VocalEditingBar from '../components/VocalEditingBar.tsx';
import { VocalCVEditProvider } from '../contexts/vocal-cv-edit.context.tsx';
import { getTemplate } from './registry';
import { CVViewMode } from './types';

interface Props {
  cv: FullCV;
  mode: CVViewMode;
  templateId: TemplateId;
  initialParams?: unknown;
  initialEditingSection?: CVEditingSection | null;
  onEditingSectionChange?: (value: boolean) => void;
  onEditCV?: (next: FullCV) => void;
}

export default function TemplateHost({
  cv,
  mode,
  templateId,
  initialParams,
  initialEditingSection,
  onEditingSectionChange,
  onEditCV,
}: Props) {
  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const parsed = template.schema.safeParse(initialParams ?? template.defaults);
  const [params, setParams] = useState<Record<string, unknown>>(
    parsed.success ? parsed.data : template.defaults,
  );

  // Keep internal params in sync when parent-provided initialParams changes
  useEffect(() => {
    const next = template.schema.safeParse(initialParams ?? template.defaults);
    setParams(next.success ? next.data : template.defaults);
  }, [initialParams, template]);

  const Display = template.Display;

  console.log('[TemplateHost] Rendering', {
    templateId: template.meta.id,
    mode,
    hasParams: !!params,
  });

  // Sections edition
  const [editingSection, setEditingSection] = useState<CVEditingSection | null>(
    initialEditingSection ?? null,
  );

  const handleSubmit = (data: FullCV) => {
    if (!onEditCV) return;
    onEditingSectionChange?.(false);
    if (editingSection === 'experiences' && data.experiences) {
      onEditCV({ ...cv, experiences: data.experiences });
    } else if (editingSection === 'personalExperiences' && data.personalExperiences) {
      onEditCV({ ...cv, personalExperiences: data.personalExperiences });
    } else if (editingSection === 'education' && data.education) {
      onEditCV({ ...cv, education: data.education });
    } else if (editingSection === 'personalInfos' && data) {
      onEditCV({ ...cv, ...data });
    } else if (editingSection === 'competences' && data.competences) {
      onEditCV({ ...cv, competences: data.competences });
    } else if (editingSection === 'languages' && data.languages) {
      onEditCV({ ...cv, languages: data.languages });
    } else if (editingSection === 'hobbiesAndInterests' && data.hobbiesAndInterests) {
      onEditCV({ ...cv, hobbiesAndInterests: data.hobbiesAndInterests });
    } else if (editingSection === 'summary' && data.summary) {
      onEditCV({ ...cv, summary: data.summary });
    } else if (editingSection === 'complementaryInfo') {
      onEditCV({
        ...cv,
        hasRQTH: data.hasRQTH,
        complementaryMentions: data.complementaryMentions,
      });
    }
    setEditingSection(null);
  };

  const handleEditingSection = (section: CVEditingSection) => {
    // Prevent double calls
    if (editingSection === section) {
      return;
    }

    setEditingSection(section);
    onEditingSectionChange?.(true);
  };

  const handleCancel = () => {
    setEditingSection(null);
    onEditingSectionChange?.(false);
  };

  if (mode !== 'edit') {
    console.log('[TemplateHost] Rendering Display', { mode, templateId: template.meta.id });
    return <Display cv={cv} params={params} mode={mode} />;
  }

  if (editingSection) {
    return (
      <VocalCVEditProvider>
        {/* pb-36 clears the fixed vocal bar below (bar ~82px + mb-16), so the
            last form row stays reachable at max scroll even with the small
            MainLayout bottom padding. */}
        <div className="flex-1 flex flex-col justify-center pb-36">
          {editingSection === 'personalInfos' && (
            <PersonalInfoForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'competences' && (
            <SkillsForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'languages' && (
            <LanguagesForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'experiences' && (
            <ExperienceForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'personalExperiences' && (
            <PersonalExperienceForm
              onSubmit={handleSubmit}
              initialData={cv}
              onCancel={handleCancel}
            />
          )}
          {editingSection === 'education' && (
            <EducationForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'hobbiesAndInterests' && (
            <HobbiesAndInterestsForm
              onSubmit={handleSubmit}
              initialData={cv}
              onCancel={handleCancel}
            />
          )}
          {editingSection === 'summary' && (
            <SummaryForm onSubmit={handleSubmit} initialData={cv} onCancel={handleCancel} />
          )}
          {editingSection === 'complementaryInfo' && (
            <ComplementaryInfoForm
              onSubmit={handleSubmit}
              initialData={cv}
              onCancel={handleCancel}
            />
          )}
        </div>
        {/* Fixed so the vocal bar stays reachable on long forms (it used to sit
            below the fold at the end of the scroll content, unclickable). */}
        <div className="fixed bottom-0 left-0 right-0 z-40 flex flex-col items-center pointer-events-none">
          <VocalEditingBar />
        </div>
      </VocalCVEditProvider>
    );
  }

  return <Display cv={cv} params={params} onEdit={handleEditingSection} mode="edit" />;
}
